import { inject, Injectable } from '@angular/core';
import { first, Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import { ApiService } from '@core/services/api.service';
import { LoadingService } from './loading';
import { SnackbarService } from './snackbar.service';
import { ForecastLoaderKey } from '@core/enums';
import { cleanHTTPParams } from '@core/utils/helpers';
import {
  ForecastReconciliationItem,
  ForecastReconciliationFilters,
  ForecastFile, InventoryItemSiteDto,
} from '@core/models';
import { environment } from '@src/environments/environment';
import { CommentableResourceService } from '@core/interfaces/commentable-resource.interface';
import { CommentLoaderKey } from '@core/enums/comment.enum';
import { Comment } from '@core/models/comment.model';

const endpoint = 'v1/forecast';

@Injectable({
  providedIn: 'root',
})
export class ForecastService implements CommentableResourceService {
  private readonly api = inject(ApiService);
  private readonly loader = inject(LoadingService);
  private readonly snack = inject(SnackbarService);

  list(filters: ForecastReconciliationFilters): Observable<ForecastReconciliationItem[]> {
    const params = new HttpParams({ fromObject: cleanHTTPParams(filters) });
    return this.api
      .get<ForecastReconciliationItem[]>(endpoint, { params })
      .pipe(first(), this.loader.set(ForecastLoaderKey.LIST_RECONCILIATIONS), this.snack.operator());
  }

  listSites(supplierId: string): Observable<InventoryItemSiteDto[]> {
    return this.api.get<InventoryItemSiteDto[]>(`${endpoint}/sites/${supplierId}`).pipe(first());
  }

  get(id: string): Observable<ForecastReconciliationItem> {
    return this.api
      .get<ForecastReconciliationItem>(`${endpoint}/${id}`)
      .pipe(first(), this.loader.set(ForecastLoaderKey.GET_RECONCILIATION), this.snack.operator());
  }

  createReconciliation(formData: FormData): Observable<ForecastReconciliationItem> {
    return this.api
      .post(endpoint, formData)
      .pipe(first(), this.loader.set(ForecastLoaderKey.CREATE_RECONCILIATION), this.snack.operator());
  }

  uploadFile(reconciliationId: string, formData: FormData): Observable<ForecastFile> {
    return this.api
      .post(`${endpoint}/${reconciliationId}/files`, formData)
      .pipe(first(), this.loader.set(ForecastLoaderKey.UPLOAD_FILE), this.snack.operator());
  }

  updateReconciliationStatus(forecastId: string, status: string): Observable<{ id: string }> {
    return this.api
      .patch(`${endpoint}/${forecastId}`, { status })
      .pipe(first(), this.loader.set(ForecastLoaderKey.UPDATE_RECONCILIATION_STATUS), this.snack.operator());
  }

  downloadFileVersion(fileId: string, versionId: string, token: string): void {
    const cid = sessionStorage.getItem('cid') as string;
    const downloadUrl = `${endpoint}/files/${fileId}/versions/${versionId}/download`;
    const url = `${environment.apiHost}/${downloadUrl}?cid=${cid}&token=${token}`;
    window.open(url, '_blank');
  }

  listComments(resourceId: string): Observable<Comment[]> {
    return this.api
      .get(`${endpoint}/files/${resourceId}/comments`)
      .pipe(first(), this.loader.set(CommentLoaderKey.LIST_COMMENTS), this.snack.operator());
  }

  createComment(resourceId: string, formData: FormData): Observable<Comment> {
    return this.api
      .post(`${endpoint}/files/${resourceId}/comments`, formData)
      .pipe(first(), this.loader.set(CommentLoaderKey.CREATE_COMMENT), this.snack.operator());
  }

  uploadNewFileVersion(fileId: string, formData: FormData): Observable<ForecastFile> {
    return this.api
      .post(`${endpoint}/files/${fileId}/versions`, formData)
      .pipe(first(), this.loader.set(ForecastLoaderKey.UPLOAD_NEW_FILE_VERSION), this.snack.operator());
  }

  updateFileVersionStatus(
    fileId: string,
    versionId: string,
    data: { status: string },
  ): Observable<ForecastFile & { reconciliationReverted?: boolean }> {
    return this.api
      .patch(`${endpoint}/files/${fileId}/versions/${versionId}`, data)
      .pipe(first(), this.loader.set(ForecastLoaderKey.UPDATE_FILE_VERSION_STATUS), this.snack.operator());
  }
}
